import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight } from "lucide-react";

const categories = [
  { id: "all", name: "Tous" },
  { id: "textile", name: "Textile" },
  { id: "impression", name: "Impression" },
  { id: "plaque", name: "Plaques & Gravure" },
  { id: "sublimation", name: "Sublimation" },
  { id: "stickers", name: "Stickers & Vinyle" },
  { id: "goodies", name: "Goodies" }
];

// Utilisation des vraies photos uploadées
const projects = [
  {
    id: 1,
    title: "T-shirt personnalisé",
    category: "textile",
    objective: "Personnalisation textile pour client",
    solution: "Impression DTF haute qualité sur t-shirt blanc",
    image: "/images/realisations/real-1.jpeg"
  },
  {
    id: 2,
    title: "Flocage textile",
    category: "textile",
    objective: "Marquage personnalisé sur vêtement",
    solution: "Flocage vinyle avec découpe précise",
    image: "/images/realisations/real-2.jpeg"
  },
  {
    id: 3,
    title: "Plaque commémorative",
    category: "plaque",
    objective: "Plaque funéraire personnalisée",
    solution: "Gravure laser sur plaque noire avec finition premium",
    image: "/images/realisations/real-3.jpeg"
  },
  {
    id: 4,
    title: "Impression photo décorative",
    category: "sublimation",
    objective: "Création visuel personnalisé avec cadre floral",
    solution: "Impression sublimation haute définition",
    image: "/images/realisations/real-4.jpeg"
  },
  {
    id: 5,
    title: "Textile événementiel",
    category: "textile",
    objective: "Vêtements pour événement",
    solution: "Impression DTF sur textiles variés",
    image: "/images/realisations/real-5.jpeg"
  },
  {
    id: 6,
    title: "Impression grand format",
    category: "impression",
    objective: "Affiche publicitaire",
    solution: "Impression numérique haute résolution",
    image: "/images/realisations/real-6.jpeg"
  },
  {
    id: 7,
    title: "Personnalisation textile",
    category: "textile",
    objective: "Marquage sur vêtement professionnel",
    solution: "Transfert numérique durable",
    image: "/images/realisations/real-7.jpeg"
  },
  {
    id: 8,
    title: "Goodies entreprise",
    category: "goodies",
    objective: "Objets promotionnels personnalisés",
    solution: "Impression sur supports variés",
    image: "/images/realisations/real-8.jpeg"
  },
  {
    id: 9,
    title: "Création graphique",
    category: "impression",
    objective: "Design et impression",
    solution: "Conception et réalisation complète",
    image: "/images/realisations/real-9.jpeg"
  },
  {
    id: 10,
    title: "T-shirt sportif",
    category: "textile",
    objective: "Équipement sportif personnalisé",
    solution: "Flocage et numérotation",
    image: "/images/realisations/real-10.jpeg"
  },
  {
    id: 11,
    title: "Impression sublimation",
    category: "sublimation",
    objective: "Visuel photo haute qualité",
    solution: "Sublimation sur support adapté",
    image: "/images/realisations/real-11.jpeg"
  },
  {
    id: 12,
    title: "Signalétique",
    category: "impression",
    objective: "Panneau informatif ou publicitaire",
    solution: "Impression sur support rigide",
    image: "/images/realisations/real-12.jpeg"
  },
  {
    id: 13,
    title: "Textile promotionnel",
    category: "textile",
    objective: "Vêtements corporate",
    solution: "Personnalisation en série",
    image: "/images/realisations/real-13.jpeg"
  },
  {
    id: 14,
    title: "Plaque personnalisée",
    category: "plaque",
    objective: "Signalétique ou décoration",
    solution: "Gravure et découpe précise",
    image: "/images/realisations/real-14.jpeg"
  },
  {
    id: 15,
    title: "Impression décorative",
    category: "sublimation",
    objective: "Création artistique",
    solution: "Impression haute définition",
    image: "/images/realisations/real-15.jpeg"
  },
  {
    id: 16,
    title: "Marquage textile",
    category: "textile",
    objective: "Personnalisation vêtement",
    solution: "Flocage ou transfert",
    image: "/images/realisations/real-16.jpeg"
  },
  {
    id: 17,
    title: "Affiche publicitaire",
    category: "impression",
    objective: "Communication visuelle",
    solution: "Impression grand format",
    image: "/images/realisations/real-17.jpeg"
  },
  {
    id: 18,
    title: "Objet personnalisé",
    category: "goodies",
    objective: "Cadeau ou promotion",
    solution: "Impression sur objet",
    image: "/images/realisations/real-18.jpeg"
  }
];

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6 }
};

export default function RealisationsPage() {
  const [activeCategory, setActiveCategory] = useState("all");

  const filteredProjects = activeCategory === "all" 
    ? projects 
    : projects.filter(p => p.category === activeCategory);

  return (
    <div data-testid="realisations-page">
      {/* Hero */}
      <section className="pt-32 pb-16 bg-secondary/30" data-testid="realisations-hero">
        <div className="container-custom">
          <motion.div {...fadeInUp}>
            <p className="eyebrow mb-3">Portfolio</p>
            <h1 className="text-4xl md:text-5xl font-bold mb-4" data-testid="realisations-title">
              Nos réalisations
            </h1>
            <p className="text-muted text-lg max-w-2xl">
              Découvrez une sélection de projets réalisés pour nos clients en Martinique. 
              Chaque projet témoigne de notre savoir-faire en impression et personnalisation.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Filter */}
      <section className="py-8 border-b border-border sticky top-16 bg-background z-40" data-testid="realisations-filter">
        <div className="container-custom">
          <div className="flex flex-wrap gap-2">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`filter-btn ${activeCategory === cat.id ? "active" : ""}`}
                data-testid={`filter-${cat.id}`}
              >
                {cat.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section className="section-padding" data-testid="realisations-gallery">
        <div className="container-custom">
          <motion.div 
            className="gallery-grid"
            layout
          >
            <AnimatePresence mode="popLayout">
              {filteredProjects.map((project) => (
                <motion.div
                  key={project.id}
                  layout
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.3 }}
                  className="card-swiss p-0 overflow-hidden group"
                  data-testid={`project-${project.id}`}
                >
                  <div className="aspect-[4/3] overflow-hidden bg-secondary">
                    <img 
                      src={project.image} 
                      alt={project.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-6">
                    <p className="font-mono text-xs text-muted mb-2 uppercase">
                      {categories.find(c => c.id === project.category)?.name}
                    </p>
                    <h3 className="font-bold text-lg mb-3">{project.title}</h3>
                    <div className="space-y-2 text-sm">
                      <p><span className="text-muted">Objectif:</span> {project.objective}</p>
                      <p><span className="text-muted">Solution:</span> {project.solution}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-foreground text-background" data-testid="realisations-cta">
        <div className="container-custom text-center">
          <motion.div {...fadeInUp}>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Un projet similaire ?
            </h2>
            <p className="text-background/70 mb-8 max-w-lg mx-auto">
              Parlez-nous de votre projet et recevez un devis personnalisé sous 24-48h.
            </p>
            <Link 
              to="/devis" 
              className="btn-outline border-background text-background hover:bg-background hover:text-foreground inline-flex items-center gap-2"
              data-testid="realisations-cta-devis"
            >
              Parlez-nous de votre projet
              <ArrowRight size={16} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
